import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const DEFAULT_DAYS = 30;

function toIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

function parseDate(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
}

export default function useDateRange(defaultDays = DEFAULT_DAYS) {
  const [params, setParams] = useSearchParams();

  const { from, to } = useMemo(() => {
    const toParam = parseDate(params.get("to"));
    const fromParam = parseDate(params.get("from"));
    const toDate = toParam || new Date();
    const fromDate =
      fromParam || new Date(toDate.getTime() - defaultDays * 86400000);
    return { from: fromDate, to: toDate };
  }, [params, defaultDays]);

  const set = useCallback(
    (next) => {
      const updated = new URLSearchParams(params);
      if (next.from) updated.set("from", toIsoDate(next.from));
      if (next.to) updated.set("to", toIsoDate(next.to));
      setParams(updated, { replace: true });
    },
    [params, setParams]
  );

  const setPreset = useCallback(
    (preset) => {
      const now = new Date();
      const today = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
      );
      let nextFrom = today;
      let nextTo = now;
      if (preset === "today") nextFrom = today;
      else if (preset === "7d") nextFrom = new Date(today.getTime() - 6 * 86400000);
      else if (preset === "30d") nextFrom = new Date(today.getTime() - 29 * 86400000);
      else if (preset === "90d") nextFrom = new Date(today.getTime() - 89 * 86400000);
      else if (preset === "thisMonth")
        nextFrom = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
      else if (preset === "lastMonth") {
        nextFrom = new Date(
          Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1)
        );
        nextTo = new Date(
          Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0, 23, 59, 59)
        );
      }
      set({ from: nextFrom, to: nextTo });
    },
    [set]
  );

  return {
    from,
    to,
    fromIso: toIsoDate(from),
    toIso: toIsoDate(to),
    set,
    setPreset,
  };
}
